import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { attachVolumeDoc } from 'app/docLinks'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { useAppSelector } from 'app/store'
import ExternalLink from 'core/components/ExternalLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import Info from 'core/components/validatedForm/Info'
import ListTableField from 'core/components/validatedForm/ListTableField'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import {
  attachVolume,
  detachVolume,
  getVolumeById,
  listNonPaginatedVolumes,
} from 'openstack/components/storage/volumes/actions'
import { nonPaginatedVolumesSelector } from 'openstack/components/storage/volumes/selectors'
import {
  canAttachVolume,
  VolumeSelectorModel,
} from 'openstack/components/storage/volumes/utils/volumeUtils'
import { getCurrentUser, humanReadableSize } from 'openstack/helpers'
import { VolumeSourceCell } from '../storage/volumes/volume-details/VolumeSourceCell'
import { formatTimestampToLocal, hasOtherUserVm, isVolumeBackedVm } from './helpers'
import { VOLUME_BACKED_VM_STRING } from './selectors'
import VolumeActionButton from './VolumeActionButton'

const useStyles = makeStyles<Theme>((theme) => ({
  nestedModal: {
    zIndex: 20000,
    '& .modal-body .progress-root': {
      height: 'auto',
      minHeight: 'auto',
    },
  },
  warning: {
    marginBottom: 16,
    border: `1px solid ${theme.palette.yellow[500]}`,
    background: theme.palette.yellow[100],
  },
}))

const enum VolumeStatus {
  Reserved = 'reserved',
  Detaching = 'detaching',
  Attaching = 'attaching',
  Available = 'available',
  InUse = 'in-use',
}
const InProgressStates = [VolumeStatus.Detaching, VolumeStatus.Attaching, VolumeStatus.Reserved]

const volumeColumns = ({
  detachVolumeFromVM,
  attachVolumeToVm,
  localLoading,
  setLocalLoading,
  vm,
}) => [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'id',
    label: 'UUID',
  },
  {
    id: 'hostName',
    label: 'Host',
    visible: !isAdmin(),
    display: false,
  },
  {
    id: 'backend',
    label: 'Volume Backend',
    visible: !isAdmin(),
    display: false,
  },
  {
    id: 'tenantName',
    label: 'Tenant',
    display: false,
  },
  {
    id: 'status',
    label: 'Status',
  },
  {
    id: 'volume_type',
    label: 'Type',
    display: false,
  },
  {
    id: 'device',
    label: 'Device',
    display: false,
  },
  {
    id: 'snapshots',
    label: 'Snapshots',
    render: (snapshots) => snapshots?.length,
    display: false,
  },
  {
    id: 'size',
    label: 'Size',
    render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  },
  {
    id: 'bootable',
    label: 'Bootable',
  },
  {
    id: 'multiattach',
    label: 'Multiattach',
    render: (val) => (val ? 'True' : 'False'),
    display: false,
  },
  {
    id: 'source',
    label: (
      <TitleWithInfoIcon
        titleText="Source"
        tooltipMsg={'The resource used to create this volume. Blank if no resource used'}
      />
    ),
    render: (val, item) => <VolumeSourceCell item={item} />,
    display: false,
  },
  {
    id: 'created_at',
    label: 'Age',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
  {
    id: 'metadata',
    label: 'Metadata',
    render: (value) => createResourceLabelsCell({ type: 'table', separator: '=' }),
    display: false,
  },
  {
    id: 'description',
    label: 'Action',
    render: (_description, item) => {
      return (
        <VolumeActionButton
          volume={item}
          vm={vm}
          detachVolumeFromVM={detachVolumeFromVM}
          attachVolumeToVm={attachVolumeToVm}
          localLoading={localLoading}
          setLocalLoading={setLocalLoading}
        />
      )
    },
  },
]

// FR-001: Core modal component for managing volume attachments to virtual machines
export default function ManageVolumesModal({ rows: vms, onClose, isOpenModal }) {
  const vm = vms?.[0]
  const classes = useStyles()
  const { role: userRole, userId } = getCurrentUser()

  // Store interval handle using ref
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null)
  const inProgressVolumeIdsRef = useRef<string[]>([])
  const isPollingRef = useRef(false)
  const [modalOpen, setModalOpen] = useState(isOpenModal)
  const [localLoading, setLocalLoading] = useState<Record<string, boolean>>({})

  // Update volumes
  const {
    update: attachVolumeFn,
    updating: attachingVolume,
    error: attachVolumeError,
    reset: resetAttachVolume,
  } = useUpdateAction(attachVolume)
  const {
    update: detachVolumeFn,
    updating: detachingVolume,
    error: detachingVolumeError,
    reset: resetDetachVolume,
  } = useUpdateAction(detachVolume)

  // Fetch volumes
  const { loading: loadingVolumes, reload } = useListAction(listNonPaginatedVolumes, {
    ignoreDependency: true,
  })
  const volumes = useAppSelector(nonPaginatedVolumesSelector)
  // FR-002: Filter volumes to show available or currently attached volumes
  const availableVolumes = useMemo(() => {
    return volumes.filter(
      (volume) =>
        canAttachVolume(volume) ||
        volume?.attachedVms?.some((attachedVm: any) => attachedVm?.id === vm.id),
    )
  }, [volumes, vm])

  // FR-005: Real-time status updates through polling mechanism
  // FR-006: Identify volumes requiring status polling
  // FR-011: Error handling for polling failures
  const inProgressVolumeIds = useMemo(() => {
    return availableVolumes
      ?.filter((volume) => InProgressStates?.includes(volume?.status))
      ?.map((volume) => volume?.id)
  }, [availableVolumes])

  // Keep ref in sync with inProgressVolumeIds to avoid stale closures
  useEffect(() => {
    inProgressVolumeIdsRef.current = inProgressVolumeIds
  }, [inProgressVolumeIds])

  // Reload volumes **once** when modal opens
  useEffect(() => {
    if (modalOpen) {
      reload(true, false)
    }
  }, [])

  const cleanupPolling = useCallback(() => {
    resetAttachVolume()
    resetDetachVolume()
    isPollingRef.current = false
  }, [resetAttachVolume, resetDetachVolume])

  // FR-006: Automatic polling for in-progress volume status updates
  // FR-007: Polling limits with MAX_RETRIES and POLL_INTERVAL constants
  const MAX_RETRIES = 10
  const POLL_INTERVAL = 5000

  useEffect(() => {
    const resetInterval = () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current)
        intervalIdRef.current = null
      }
      isPollingRef.current = false
    }

    // No in-progress volumes - nothing to poll
    if (inProgressVolumeIds.length === 0) {
      cleanupPolling()
      resetInterval()
      return undefined
    }

    // Already polling
    if (isPollingRef.current) return undefined

    // Set loading state for in-progress volumes
    setLocalLoading((prev) => {
      const next = { ...prev }
      inProgressVolumeIds.forEach((id) => (next[id] = true))
      return next
    })

    let retryCount = 0
    isPollingRef.current = true

    const pollVolumes = async () => {
      // Use ref to get current in-progress IDs (avoids stale closure)
      const currentInProgressIds = inProgressVolumeIdsRef.current

      // If no volumes to poll, stop
      if (currentInProgressIds.length === 0) {
        cleanupPolling()
        resetInterval()
        return
      }

      retryCount++

      try {
        const fetchedVolumes = await Promise.all(
          currentInProgressIds.map((id) => getVolumeById({ id })),
        )

        // Update loading state based on fetched status
        setLocalLoading((prev) => {
          const next = { ...prev }
          fetchedVolumes.forEach((volume) => {
            if (
              volume?.status === VolumeStatus.Available ||
              volume?.status === VolumeStatus.InUse
            ) {
              next[volume.id] = false
            }
          })
          return next
        })

        // Check if all volumes are done
        const allDone = fetchedVolumes.every(
          (v) => v?.status === VolumeStatus.Available || v?.status === VolumeStatus.InUse,
        )

        // FR-006: Check if all volume operations are complete
        // FR-007: Enforce maximum retry limit for polling
        if (allDone || retryCount >= MAX_RETRIES) {
          cleanupPolling()
          resetInterval()
        }
      } catch (err) {
        console.error('Error polling volumes:', err)
        // FR-011: Error handling during polling with retry limit enforcement
        if (retryCount >= MAX_RETRIES) {
          cleanupPolling()
          resetInterval()
        }
      }
    }

    // Start polling immediately, then continue at interval
    pollVolumes()
    // FR-006: Start automatic polling for volume status updates
    // FR-007: Apply polling interval and retry limits
    intervalIdRef.current = setInterval(pollVolumes, POLL_INTERVAL)

    // Cleanup on unmount or when inProgressVolumeIds changes
    return () => {
      resetInterval()
    }
  }, [inProgressVolumeIds.join(',')])

  // FR-005: API integration for volume attachment operations
  // FR-011: Error handling for failed volume operations
  const attachVolumeToVm = useCallback(
    async ({ id }, deleteOnTermination: boolean) => {
      const body = {
        volumeAttachment: { volumeId: id, delete_on_termination: deleteOnTermination },
      }
      const response = await attachVolumeFn({ vmId: vm.id, id, body })
      if (!response.success) {
        setLocalLoading((prev) => ({ ...prev, [id]: false }))
      }
    },
    [vm, attachVolumeFn],
  )

  // FR-005: API integration for volume detachment operations
  // FR-011: Error handling for failed volume operations
  const detachVolumeFromVM = useCallback(
    async ({ id }) => {
      const response = await detachVolumeFn({ vmId: vm.id, volumeId: id })
      if (!response.success) {
        setLocalLoading((prev) => ({ ...prev, [id]: false }))
      }
    },
    [vm, detachVolumeFn],
  )

  // FR-012: Modal cleanup with polling interval cleanup and state reset
  const onModalClose = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }
    cleanupPolling()
    onClose()
    setModalOpen(false)
  }, [onClose, cleanupPolling])

  return (
    <ModalForm
      open
      title={`Manage Volumes`}
      onClose={onModalClose}
      submitting={attachingVolume || detachingVolume}
      error={attachVolumeError || detachingVolumeError}
      // FR-011: Display API errors to users with error messaging
      submitTitle={`Attach Volumes`}
      maxWidth={1200}
      closeText="Close"
    >
      {/* FR-010: Service health indicators for hypervisor and block storage services */}
      <ServiceUnhealthyInfo action="Volume Actions" hypervisor blockStorage />
      {/* FR-008: Ephemeral storage warning with documentation link */}
      {!isVolumeBackedVm(vm) && (
        <Info className={classes.warning} error>
          This virtual machine uses an ephemeral root disk. Read{' '}
          <ExternalLink url={attachVolumeDoc}>this article</ExternalLink> to understand the{' '}
          <b>caveats of attaching volumes</b> to such VMs.
        </Info>
      )}
      <FormFieldSection title={`Volumes to attach or detach from VM: ${vm.name || vm.id}`}>
        {/* FR-009: Cross-user operation warning for member users */}
        {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
          <Info error>NOTE: You are performing an operation on another user's VM.</Info>
        )}
        <ListTableField
          id="attachDetachVolumes"
          uniqueIdentifier="id"
          searchTargets={['name', 'id', 'status']}
          data={availableVolumes}
          loading={loadingVolumes}
          columns={volumeColumns({
            attachVolumeToVm,
            detachVolumeFromVM,
            localLoading,
            setLocalLoading,
            vm,
          })}
          onReload={() => reload(true, true)}
          emptyText="No volumes available"
          // FR-002: Empty state handling when no volumes are available
          showCheckboxes={false}
        />
      </FormFieldSection>
    </ModalForm>
  )
}
